/* eslint-disable @next/next/no-img-element */
'use client';

import { useBreakpointHooks } from '@/utils/break-points.provider';
import Button from '@ui/Button';
import Link from '@ui/Link';
import Avatar from '@ui/Avatar';
import { getPostImage, getPostLink } from '@utils/post.helper';
import { isArray } from 'lodash-es';
import Image from 'next/image';
import React from 'react';
import { HiOutlineVideoCamera } from 'react-icons/hi';
import { MdOutlineChevronLeft, MdOutlineChevronRight } from 'react-icons/md';
import { useSpringCarousel } from 'react-spring-carousel';
import PostType, { CampaignType } from '@/constants/postType.constants';
import { useFetchFeaturedPosts } from '@/actions/feed.actions';
import VideoPlayer from '@/components/VideoPlayer';
import Reactions from '@/components/next-page-components/Reactions';

function FeaturedPostsCarousel(props) {
  const { initialData } = props;

  const { data: posts } = useFetchFeaturedPosts(initialData);

  const isExtraSmallScreen = useBreakpointHooks((bp) => bp.useSmaller('sm'));
  const isSmallScreen = useBreakpointHooks((bp) => bp.useSmaller('md'));
  const isMediumScreen = useBreakpointHooks((bp) => bp.useSmaller('lg'));
  const isLargeScreen = useBreakpointHooks((bp) => bp.useSmaller('xl'));

  const cardWidth = 220;

  const itemsPerSlide = isExtraSmallScreen
    ? 2
    : isSmallScreen
    ? 2.8
    : isMediumScreen
    ? 2.6
    : isLargeScreen
    ? 3.5
    : 4.4;

  const carouselItems = [...posts];
  const emptySlotCount = Math.ceil(itemsPerSlide - carouselItems.length);
  if (emptySlotCount > 0) {
    const emptyItems = Array(emptySlotCount)
      .fill()
      .map((_, index) => ({ id: `empty-${index}`, empty: true }));
    carouselItems.push(...emptyItems);
  }

  const renderPostImage = (image) => {
    // if (image.type === 'logo') {
    //   return (
    //     <Avatar
    //       src={image.url}
    //       alt="Logo"
    //       size="lg"
    //       className="scale-75 border bg-black p-1 m-auto"
    //     />
    //   );
    // }
    return (
      <Image
        src={image.url || ''}
        alt="post image"
        fill
        className="rounded bg-black object-contain"
      />
    );
  };

  const renderPostType = (post) => {
    switch (post.sourcePostType) {
      case PostType.Campaign: {
        const campaignType = post.sourcePostData.campaignType;
        if (campaignType === CampaignType.FundRaise) {
          return 'Fundraiser';
        } else if (campaignType === CampaignType.CollectGoods) {
          return 'In-Kind Fundraiser';
        }
        return 'Fundraiser';
      }
      case PostType.Volunteering:
        return 'Volunteering Opportunity';
      case PostType.Project:
        return 'Project';
      case PostType.CrowdFunding:
        return 'Fundraiser';
      case PostType.UserPost:
        return 'Post';
      default:
        return 'Post';
    }
  };

  const renderItem = (post) => {
    if (post.empty) return <div key={post.id} className="h-full w-full" />;

    const hasCelebrationVideo =
      post.sourcePostData && post.sourcePostData.attachmentType === 'video';
    const image = getPostImage(post);
    const hasVideo =
      post?.renderProps?.videoUrls &&
      isArray(post.renderProps.videoUrls) &&
      post.renderProps.videoUrls[0];

    let postLink = getPostLink(post);

    const likeCount = post.totalReactions || 0;
    const commentCount = post.totalComments || 0;

    return (
      <Link href={postLink} key={post.id}>
        <div className="mr-4 flex w-[210px] min-w-[210px] flex-shrink-0 flex-grow-0 flex-col rounded-lg border border-gray-200 bg-white p-2 shadow-md transition hover:shadow-lg">
          <span className="mb-1 line-clamp-1 text-12 text-text-secondary">
            {renderPostType(post)}
          </span>
          <div className="mb-2 line-clamp-1 text-14 font-medium leading-tight text-text-primary">
            {post.renderProps.title}
          </div>
          <div className="relative mb-2 flex h-[100px] w-full items-center justify-center overflow-hidden rounded-md bg-gray-100">
            {hasVideo || hasCelebrationVideo ? (
              <VideoPlayer
                src={
                  hasCelebrationVideo
                    ? post.renderProps.imageUrls
                    : post.renderProps.videoUrls[0]
                }
                className="h-full w-full rounded object-cover">
                <HiOutlineVideoCamera className="h-10 w-10 text-primary" />
              </VideoPlayer>
            ) : (
              <div className="relative aspect-video w-full overflow-hidden">
                {renderPostImage(image)}
              </div>
            )}
          </div>
          <div className="mt-auto flex items-center gap-2 text-12 text-text-secondary">
            <Reactions reactions={post.reactions} onClick={() => {}} />
            <span className="flex items-center gap-1">
              {likeCount.toLocaleString()} {likeCount > 0 ? '' : 'like'}
            </span>
            <span className="text-text-secondary">·</span>
            <span className="flex items-center gap-1">
              {commentCount.toLocaleString()}{' '}
              {commentCount > 1 ? 'comments' : 'comment'}
            </span>
          </div>
        </div>
      </Link>
    );
  };

  const canNavigate = posts.length > itemsPerSlide;

  const {
    carouselFragment,
    slideToPrevItem,
    slideToNextItem,
    getCurrentActiveItem,
  } = useSpringCarousel({
    slideAmount: cardWidth,
    withLoop: canNavigate,
    itemsPerSlide: itemsPerSlide,
    gutter: 0,
    items: carouselItems.map((post) => ({
      id: post.id,
      renderItem: renderItem(post),
    })),
  });

  const handleNextClick = () => {
    slideToNextItem();
  };

  const handlePrevClick = () => {
    slideToPrevItem();
  };

  return (
    <div className="group w-full overflow-hidden">
      <div className="relative flex w-[calc(100%+1rem)] items-center justify-center gap-2 lg:gap-11">
        <div className="flex-1 gap-3 overflow-hidden">{carouselFragment}</div>
        <div className="absolute left-2 top-1/2">
          <Button
            variant="icon"
            size="xs"
            className="bg-body-background opacity-90 group-hover:bg-body-background"
            onClick={handlePrevClick}
            disabled={!canNavigate}>
            <MdOutlineChevronLeft
              className={`h-7 w-7 opacity-100 ${
                canNavigate ? 'text-text-disabled' : 'text-body-background'
              }`}
            />
          </Button>
        </div>
        <div className="absolute right-6 top-1/2">
          <Button
            variant="icon"
            size="xs"
            className="bg-body-background opacity-90 group-hover:bg-body-background"
            onClick={handleNextClick}
            disabled={!canNavigate}>
            <MdOutlineChevronRight
              className={`h-7 w-7 opacity-100 ${
                canNavigate ? 'text-text-disabled' : 'text-body-background'
              }`}
            />
          </Button>
        </div>
      </div>
    </div>
  );
}

export default React.memo(FeaturedPostsCarousel);
